import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import { getInvoiceNumberLabel } from '../utils/invoiceNumber';

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const defaultRange = () => {
  const today = new Date();
  return {
    fromDate: formatInputDate(new Date(today.getFullYear(), today.getMonth(), 1)),
    toDate: formatInputDate(today),
  };
};

const formatCurrency = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const csvHeaders = [
  { label: 'Vehicle No', key: 'vehicle_no' },
  { label: 'Vehicle Model', key: 'vehicle_model' },
  { label: 'Customer', key: 'customer_name' },
  { label: 'Invoice No', key: 'display_inv_no' },
  { label: 'Invoice Date', key: 'inv_date' },
  { label: 'Item', key: 'item_name' },
  { label: 'Quantity', key: 'qty' },
  { label: 'Sales Amount', key: 'sales_amount' },
  { label: 'Cost', key: 'cost_amount' },
  { label: 'Profit', key: 'profit' },
  { label: 'Invoice Total', key: 'invoice_total' },
];

function VehicleWiseInvoiceReport() {
  const initialRange = useMemo(defaultRange, []);
  const [vehicle, setVehicle] = useState('');
  const [fromDate, setFromDate] = useState(initialRange.fromDate);
  const [toDate, setToDate] = useState(initialRange.toDate);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('https://serveravotasautofleet.ccsoftwares.com/sales/vehicleWiseInvoices', {
        params: {
          vehicle: vehicle.trim() || undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        },
      });
      setRows((response.data || []).map((row) => ({
        ...row,
        display_inv_no: getInvoiceNumberLabel(row.inv_no),
        inv_date: row.inv_date ? String(row.inv_date).slice(0, 10) : '-',
      })));
      setPage(0);
    } catch (requestError) {
      setRows([]);
      setError(requestError.response?.data?.error || 'Unable to load vehicle-wise invoices.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, vehicle]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const totals = useMemo(() => rows.reduce((summary, row) => ({
    sales: summary.sales + Number(row.sales_amount || 0),
    cost: summary.cost + Number(row.cost_amount || 0),
    profit: summary.profit + Number(row.profit || 0),
  }), { sales: 0, cost: 0, profit: 0 }), [rows]);

  const paginatedRows = rowsPerPage === -1
    ? rows
    : rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Vehicle-Wise Invoices</Typography>
          <Typography variant="body2" color="text.secondary">
            Active invoice lines and profit grouped by the existing customer vehicle relationship.
          </Typography>
        </Box>
        <CSVLink data={rows} headers={csvHeaders} filename="vehicle-wise-invoices.csv" style={{ textDecoration: 'none' }}>
          <Button variant="contained" startIcon={<DownloadIcon />} disabled={!rows.length}>Download CSV</Button>
        </CSVLink>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Vehicle Number" value={vehicle} onChange={(event) => setVehicle(event.target.value)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth type="date" label="From Date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth type="date" label="To Date" value={toDate} onChange={(event) => setToDate(event.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={fetchRows}>Refresh</Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          ['Sales', totals.sales],
          ['Cost', totals.cost],
          ['Profit', totals.profit],
        ].map(([label, value]) => (
          <Grid item xs={12} md={4} key={label}>
            <Paper sx={{ p: 2, bgcolor: '#F1F5F9' }}>
              <Typography variant="body2" color="text.secondary">Total {label}</Typography>
              <Typography variant="h5" fontWeight={700}>Rs. {formatCurrency(value)}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {error ? <Typography color="error" sx={{ mb: 2 }}>{error}</Typography> : null}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#000' }}>
            <TableRow>
              {['Vehicle', 'Model', 'Customer', 'Invoice', 'Invoice Date', 'Item', 'Qty', 'Sales', 'Cost', 'Profit', 'Invoice Total'].map((header) => (
                <TableCell key={header} sx={{ color: '#fff', fontWeight: 600 }}>{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={11} align="center"><CircularProgress size={28} /></TableCell></TableRow>
            ) : paginatedRows.length ? paginatedRows.map((row) => (
              <TableRow key={row.invdetails_key}>
                <TableCell>{row.vehicle_no || '-'}</TableCell>
                <TableCell>{row.vehicle_model || '-'}</TableCell>
                <TableCell>{row.customer_name || '-'}</TableCell>
                <TableCell>{row.display_inv_no}</TableCell>
                <TableCell>{row.inv_date}</TableCell>
                <TableCell>{row.item_name}</TableCell>
                <TableCell>{row.qty}</TableCell>
                <TableCell>{formatCurrency(row.sales_amount)}</TableCell>
                <TableCell>{formatCurrency(row.cost_amount)}</TableCell>
                <TableCell>{formatCurrency(row.profit)}</TableCell>
                <TableCell>{formatCurrency(row.invoice_total)}</TableCell>
              </TableRow>
            )) : (
              <TableRow><TableCell colSpan={11} align="center">No active invoices match these filters.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
      />
    </Box>
  );
}

export default VehicleWiseInvoiceReport;
